import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Send, MessageCircle, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const STATUS_TONE = { sent: "success", queued: "warn", failed: "danger", inapp: "info" };
const SEVERITY_BORDER = { info: "border-l-primary", success: "border-l-success", warning: "border-l-warn", danger: "border-l-danger" };

export default function Notifications() {
  const toast = useToast();
  const navigate = useNavigate();
  const { isRole } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);
  const canManage = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "WAREHOUSE");

  const load = () => api.get("/notifications").then(({ data }) => setData(data));
  useEffect(() => { load();
    // eslint-disable-next-line
  }, []);
  if (!data) return <PageLoader />;

  const openNote = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* */ }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); toast.success(t("All marked read")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed to mark read")); }
  };
  const flush = async () => {
    try {
      const { data: r } = await api.post("/notifications/flush");
      toast.info(r.reason || `${t("Flushed")} ${r.flushed} ${t("notifications")}`);
      load();
    } catch (e) {
      toast.error(e.response?.status === 403 ? t("You do not have permission to send notifications") : (e.response?.data?.detail || t("Failed")));
    }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Notification Center")} subtitle={t("WhatsApp notification log & delivery queue")} icon={Bell}>
        <Button variant="outline" size="sm" onClick={markAll} data-testid="mark-all-read"><CheckCheck size={16} /> {t("Mark all read")}</Button>
        {canManage && <Button size="sm" onClick={flush} data-testid="flush-queue"><Send size={16} /> {t("Send Queued")}</Button>}
      </PageHeader>

      <Card className="mb-5 flex items-center gap-3 p-4">
        <MessageCircle size={20} className={data.whatsapp_configured ? "text-success" : "text-warn"} />
        <div className="flex-1">
          <p className="text-sm font-semibold text-ink">{t("WhatsApp Cloud API")}: {data.whatsapp_configured ? t("Connected") : t("Not configured")}</p>
          <p className="text-xs text-ink-soft">
            {data.whatsapp_configured
              ? t("Notifications are delivered live via WhatsApp.")
              : t("Notifications are stored & queued. Add Meta credentials to activate live sending — no workflow changes needed.")}
          </p>
        </div>
        <Badge tone={data.whatsapp_configured ? "success" : "warn"}>{data.whatsapp_configured ? t("Live") : t("Queued")}</Badge>
      </Card>

      {data.notifications.length === 0 ? (
        <EmptyState title={t("No notifications yet")} subtitle={t("System events like order submissions and low-stock alerts will appear here.")} />
      ) : (
        <div className="space-y-2">
          {data.notifications.map((n) => (
            <Card key={n.id} onClick={() => openNote(n)}
              className={`flex items-start gap-3 p-4 ${!n.read ? `border-l-4 ${SEVERITY_BORDER[n.severity] || "border-l-primary"}` : ""} ${n.link ? "cursor-pointer transition hover:shadow-lg" : ""}`} data-testid="notification-item">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-light text-primary"><Bell size={16} /></div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-ink">{n.title}</p>
                  <Badge tone={STATUS_TONE[n.status] || "mute"}>{t(n.status)}</Badge>
                </div>
                <p className="text-sm text-ink-soft">{n.body}</p>
                <p className="mt-1 text-xs text-ink-mute">{(n.created_at || "").slice(0, 16).replace("T", " ")} · {n.event}</p>
              </div>
              {n.link && <ChevronRight size={16} className="mt-1 shrink-0 text-ink-mute" />}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
